import { DepartmentLabel, useDepartmentText } from 'model/DepartmentTerminology';
import { useForm } from '@mantine/form';
import { isEmpty, noop, uniq } from 'lodash';
import { FormProps } from 'components/forms/FormProps';
import useLoadingAction from 'hooks/use-loading-action';
import { Box, Center, Group, SimpleGrid, Stack, TextInput, Tooltip, useMantineTheme } from '@mantine/core';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import { IconArrowUpLeft, IconCheck, IconPlus, IconTrashX } from '@tabler/icons-react';
import FormWrapper from 'components/forms/FormWrapper';
import FormInputGroup from 'components/forms/FormInputGroup';
import { OrganizationSelect } from 'components/selects/OrganizationSelect';
import { DepartmentSelect } from 'components/selects/DepartmentSelect';
import { DeviceTypeSelect } from 'components/selects/DeviceTypeSelect';
import { useEffect, useMemo, useState } from 'react';
import { RevisionTechnicianSelect } from 'components/selects/RevisionTechnicianSelect';
import RevisionTypeSelect from 'components/selects/RevisionTypeSelect';
import P1Regular from 'components/typography/P1Regular';
import P1Medium from 'components/typography/P1Medium';
import { usePickDevices } from 'components/modals/pick-devices-modal/PickDevicesProvider';
import P2Medium from 'components/typography/P2Medium';
import RevisionTermSelect from 'components/inputs/revision-term-select/RevisionTermSelect';
import { DateInput } from 'components/inputs/DateInput';
import { useConfirm } from 'components/modals/message/MessageProvider';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import { getDeviceType } from 'model/DeviceType';
import { useDirtyFormHandler } from 'hooks/use-dirty-form-handler';
import DeviceStructureIcon from 'components/DeviceStructureIcon';
import { millisecondsInHour } from 'date-fns';
import { FEATURE_TOGGLE_REVISION_FORM_ALLOW_CREATE_WITHOUT_TECHNICIAN as ALLOW_EMPTY_TECHNICIAN } from 'env';
import DeviceTypeIcon from 'components/device/DeviceTypeIcon';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import Button from 'components/Button';

export interface RevisionDevice {
  deviceId: number;
  deviceName: string;
  termId: string; // Which term (revision plan) of the device is selected in the revision
  deviceTypeId: number;
  deviceSubtypeId: number;
  deviceSubtypeName: string;
  checked: boolean;
  manufacturer?: string;
  type: string;
  serialNumber?: string;
  manufactured?: number;
  internalPossessionsNumber?: string;
  building?: string;
  location?: string;
  organization?: {
    organizationId: number;
    organizationName: string;
  };
  parentDevice?: {
    deviceId: number;
    deviceName: string;
  };
  childDevices?: {
    deviceId: number;
    deviceName: string;
  }[];
  structure: string;
  revisionPlan?: {
    revisionPlanId: number;
    description: string;
    date?: string;
    revisionPeriod: number;
    neverExecuted: boolean;
    deviceTypeId: number;
  }[];
}

export interface RevisionFormData {
  subKind?: string;
  revisionName: string;
  deadline?: string;
  organizationId: string;
  departmentId: string;
  deviceTypeId: string;
  devices: {
    [deviceId: string]: RevisionDevice;
  };
  technicianId: string;
}

export const revisionFormInitialValues: RevisionFormData = {
  subKind: 'revision',
  deadline: '',
  revisionName: '',
  organizationId: '',
  departmentId: '',
  deviceTypeId: '',
  devices: {},
  technicianId: '',
};

export interface RevisionFormProps extends FormProps<RevisionFormData> {
  readOnly?: Partial<Record<keyof RevisionFormData, boolean>>;
  activeDeviceTypesOnly?: boolean;
  allowEmptyTechnician?: boolean;
  primaryButtonText?: string;
  primaryIcon?: React.ReactNode;
  context: 'create' | 'edit';
  kind: 'revision' | 'form';
}

/**
 * Form for creating and editing revision.
 */
export default function RevisionForm({
  initialValues = revisionFormInitialValues,
  onSubmit = noop,
  readOnly = {},
  activeDeviceTypesOnly = true,
  allowEmptyTechnician = ALLOW_EMPTY_TECHNICIAN,
  primaryButtonText = 'Uložiť',
  primaryIcon = <IconCheck stroke={1.5} size={24} />,
  context,
  kind,
}: RevisionFormProps) {
  const departmentText = useDepartmentText();
  const theme = useMantineTheme();
  const { pickDevices } = usePickDevices();
  const { confirm } = useConfirm();
  const [{ loading }, submit] = useLoadingAction(onSubmit);
  const [changeDepartmentId, setChangeDepartmentId] = useState<string | null>(null);
  const [changeOrganizationId, setChangeOrganizationId] = useState<string | null>(null);
  const [changeDeviceTypeId, setChangeDeviceTypeId] = useState<string | null>(null);
  const { isTablet } = useResponsiveDesign();

  const form = useForm<RevisionFormData>({
    initialValues,
    validate: {
      revisionName: createValidator([required]),
      organizationId: createValidator([required]),
      departmentId: createValidator([required]),
      deviceTypeId: createValidator([required]),
      technicianId: allowEmptyTechnician ? undefined : createValidator([required]),
      subKind: kind === 'revision' ? undefined : createValidator([required]),
      devices: (value) => {
        if (Object.values(value).every(({ checked }) => !checked)) {
          return 'Musíte vybrať aspoň jedno zariadenie.';
        }
      },
    },
  });

  useDirtyFormHandler(form);

  const pickedDevices = useMemo(
    () => Object.values(form.values.devices).filter(({ checked }) => checked),
    [form.values.devices]
  );

  const pickedDeviceIds = useMemo(() => uniq(pickedDevices.map(({ deviceId }) => deviceId)), [pickedDevices]);

  const pickedParent = useMemo(() => pickedDevices.find(({ structure }) => structure === 'parent'), [pickedDevices]);
  const pickedChildren = useMemo(() => pickedDevices.filter(({ structure }) => structure === 'child'), [pickedDevices]);

  const devicesInDisplayOrder = useMemo(
    () => (pickedParent ? [pickedParent, ...pickedChildren] : pickedDevices),
    [pickedDevices, pickedParent, pickedChildren]
  );

  /** Opens the modal for picking devices and adds the picked devices to the form. */
  const addDevices = () => {
    pickDevices({
      title: 'Pridať zariadenia',
      alreadyPickedDeviceIds: pickedDeviceIds,
      revisionDeviceTypeId: Number(form.values.deviceTypeId),
      departmentId: Number(form.values.departmentId),
      onPick: ({ pickedDevices }) => {
        Object.values(pickedDevices).forEach((device) => {
          form.setFieldValue(`devices.${device.deviceId}`, device);
        });
      },
    });
  };

  /** Confirms the action to delete the device from the revision. */
  const confirmDeleteDevice = (deviceId: number) => {
    confirm({
      title: pickedParent ? 'Odobranie zariadení' : 'Odobranie zariadenia',
      content: pickedParent
        ? 'Odobraním nadradeného zariadenia odoberiete aj všetky podriadené zariadenia. Naozaj chcete pokračovať?'
        : 'Naozaj chcete odobrať toto zariadenie?',
      onConfirm: () => {
        if (pickedParent) {
          pickedDeviceIds.forEach((deviceId) => {
            form.setFieldValue(`devices.${deviceId}.checked`, false);
          });
        } else {
          form.setFieldValue(`devices.${deviceId}.checked`, false);
        }
      },
    });
  };

  /** Handles the form submission. */
  const onFormSubmit = form.onSubmit((values) => {
    if (allowEmptyTechnician && !values.technicianId) {
      confirm({
        title: kind === 'revision' ? 'Pokračovať bez revízneho technika?' : 'Pokračovať bez vypracovateľa?',
        content:
          kind === 'revision'
            ? 'Táto revízna správa nebude môcť byť posunutá ďalej, dokým nebude priradený revízny technik. Technika môžete priradiť aj neskôr. Naozaj chcete pokračovať?'
            : 'Tento formulár nebude môcť byť posunutý ďalej, dokým nebude priradený vypracovateľ. Vypracovateľa môžete priradiť aj neskôr. Naozaj chcete pokračovať?',
        primaryButtonText: 'Pokračovať',
        secondaryButtonText: 'Späť',
        onConfirm: () => submit(values),
      });

      return;
    }

    return submit(values);
  });

  // Organization change confirmation
  useEffect(() => {
    if (changeOrganizationId) {
      confirm({
        title: 'Naozaj chcete zmeniť organizáciu?',
        content: 'Zmena organizácie odoberie všetky vybrané zariadenia. Naozaj chcete pokračovať?',
        returnFocus: false,
        bypassConfirm: isEmpty(pickedDevices),
        onConfirm: () => {
          form.getInputProps('organizationId').onChange(changeOrganizationId);
          form.getInputProps('departmentId').onChange('');
          form.getInputProps('deviceTypeId').onChange('');
          form.getInputProps('technicianId').onChange('');
          form.setFieldValue('devices', {});
        },
        onClose: () => setChangeOrganizationId(null),
      });
    }
  }, [changeOrganizationId]);

  // Department change confirmation
  useEffect(() => {
    if (changeDepartmentId) {
      confirm({
        title: (
          <DepartmentLabel
            objectLabel="Naozaj chcete zmeniť položku úrovne?"
            departmentLabel="Naozaj chcete zmeniť stredisko?"
          />
        ),
        content: (
          <DepartmentLabel
            objectLabel="Zmena položky úrovne odoberie všetky vybrané zariadenia. Naozaj chcete pokračovať?"
            departmentLabel="Zmena strediska odoberie všetky vybrané zariadenia. Naozaj chcete pokračovať?"
          />
        ),
        returnFocus: false,
        bypassConfirm: isEmpty(pickedDevices),
        onConfirm: () => {
          form.getInputProps('departmentId').onChange(changeDepartmentId);
          form.getInputProps('deviceTypeId').onChange('');
          form.getInputProps('technicianId').onChange('');
          form.getInputProps('subKind').onChange('');
          form.setFieldValue('devices', {});
        },
        onClose: () => setChangeDepartmentId(null),
      });
    }
  }, [changeDepartmentId]);

  // Device type change confirmation
  useEffect(() => {
    if (changeDeviceTypeId) {
      confirm({
        title: 'Naozaj chcete zmeniť typ zariadenia?',
        content: readOnly.devices
          ? 'Vybrané zariadenia zostanú zachované. Naozaj chcete pokračovať?'
          : 'Zmena typu zariadenia odoberie všetky vybrané zariadenia. Naozaj chcete pokračovať?',
        returnFocus: false,
        bypassConfirm: isEmpty(pickedDevices),
        onConfirm: () => {
          form.getInputProps('deviceTypeId').onChange(changeDeviceTypeId);
          form.getInputProps('technicianId').onChange('');
          if (!readOnly.devices) {
            form.setFieldValue('devices', {});
          }
          setChangeDeviceTypeId(null);
        },
        onClose: () => setChangeDeviceTypeId(null),
      });
    }
  }, [changeDeviceTypeId]);

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '2fr 2fr 1fr',
    gap: 0,
  };

  return (
    <FormWrapper
      onSubmit={onFormSubmit}
      loading={loading}
      primaryButtonText={primaryButtonText}
      primaryIcon={primaryIcon}
      skipSecondaryButtonConfirm={!form.isDirty()}
    >
      <FormInputGroup groupTitle="Základné informácie">
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <TextInput label="Názov" size="lg" name="objekto-revisionName" {...form.getInputProps('revisionName')} />
          <DateInput
            label="Deadline (nepovinné)"
            size="lg"
            placeholder={`napr. ${new Date(Date.now() + 30 * 24 * millisecondsInHour).toLocaleDateString('sk')})`}
            clearable
            {...form.getInputProps('deadline')}
          />
        </SimpleGrid>
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <OrganizationSelect
            label="Organizácia"
            size="lg"
            showDiscarded={false}
            readOnly={readOnly.organizationId}
            disabled={readOnly.organizationId}
            permissionSlug={kind === 'revision' ? 'assign-revisions' : 'assign-forms'}
            name="objekto-organizationId"
            autoSelectSingleResult
            value={changeOrganizationId ?? form.values.organizationId}
            onChange={(val) => setChangeOrganizationId(val)}
          />
          {form.values.organizationId !== '' && (
            <DepartmentSelect
              label={departmentText('Položka úrovne', 'Stredisko')}
              size="lg"
              showDiscarded={false}
              organizationId={Number(form.values.organizationId)}
              readOnly={readOnly.departmentId}
              disabled={readOnly.departmentId}
              permissionSlug={kind === 'revision' ? 'assign-revisions' : 'assign-forms'}
              name="objekto-departmentId"
              autoSelectSingleResult
              value={changeDepartmentId ?? form.values.departmentId}
              onChange={(val) => setChangeDepartmentId(val)}
            />
          )}
        </SimpleGrid>
        {kind === 'form' && form.values.departmentId && (
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <RevisionTypeSelect
              label="Typ formulára"
              size="lg"
              excludeRevision
              departmentId={Number(form.values.departmentId)}
              name="objekto-subKind"
              readOnly={readOnly.subKind}
              autoSelectSingleResult
              {...form.getInputProps('subKind')}
              value={form.values.subKind}
              onChange={(value) => {
                form.getInputProps('technicianId').onChange('');
                form.getInputProps('subKind').onChange(value);
              }}
            />
          </SimpleGrid>
        )}
        {!!form.values.organizationId && !!form.values.departmentId && (
          <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
            <DeviceTypeSelect
              label="Zariadenia"
              activeOnly={activeDeviceTypesOnly}
              departmentId={Number(form.values.departmentId)}
              placeholder={readOnly.deviceTypeId ? getDeviceType(Number(form.values.deviceTypeId))?.name : ''}
              size="lg"
              readOnly={readOnly.deviceTypeId}
              disabled={readOnly.deviceTypeId}
              name="objekto-deviceTypeId"
              autoSelectSingleResult
              value={changeDeviceTypeId ?? form.values.deviceTypeId}
              onChange={(val) => setChangeDeviceTypeId(val)}
            />
            {!!form.values.deviceTypeId && (kind === 'revision' || !!form.values.subKind) && (
              <RevisionTechnicianSelect
                label={kind === 'revision' ? 'Revízny technik' : 'Vypracovateľ'}
                kind={kind === 'revision' ? 'revision' : form.values.subKind!}
                size="lg"
                readOnly={readOnly.technicianId}
                disabled={readOnly.technicianId}
                departmentId={Number(form.values.departmentId)}
                deviceTypeId={Number(form.values.deviceTypeId)}
                name="objekto-technicianId"
                autoSelectSingleResult={!allowEmptyTechnician}
                allowDeselect={allowEmptyTechnician}
                clearable={allowEmptyTechnician}
                {...form.getInputProps('technicianId')}
              />
            )}
          </SimpleGrid>
        )}
      </FormInputGroup>

      {!!form.values.deviceTypeId && !!form.values.departmentId && (
        <FormInputGroup groupTitle="Výber zariadení">
          {isEmpty(pickedDevices) ? (
            <Stack spacing={8} align="flex-start">
              <P1Regular color="gray.6">Zatiaľ ste nevybrali žiadne zariadenia.</P1Regular>
              {!readOnly.devices && (
                <Button variant="subtle" onClick={addDevices} leftIcon={<IconPlus stroke="1.5" />}>
                  Pridať
                </Button>
              )}
              <P2Medium color="red.6">{form.errors.devices}</P2Medium>
            </Stack>
          ) : (
            <Stack spacing={24}>
              <Stack spacing={0}>
                <Group style={{ ...gridStyle, borderRadius: '4px' }} bg="gray.0">
                  <Box p={16}>
                    <P1Medium>Názov</P1Medium>
                  </Box>
                  <Box p={16}>
                    <P1Medium>Termín</P1Medium>
                  </Box>
                  <Box p={16} />
                </Group>
                {devicesInDisplayOrder.map((device) => (
                  <Group
                    key={device.deviceId}
                    style={{ ...gridStyle, borderBottom: `1px solid ${theme.colors.gray[1]}` }}
                    spacing={0}
                    h={72}
                  >
                    <Group px={16} spacing={8} align="center">
                      {pickedParent && device.structure !== 'parent' && (
                        <Center pl={16}>
                          <Tooltip label="Podradené zariadenie" openDelay={200} withinPortal>
                            <IconArrowUpLeft size={16} stroke={1.8} />
                          </Tooltip>
                        </Center>
                      )}
                      <DeviceTypeIcon typeId={device.deviceTypeId} />
                      <Group spacing={4} noWrap align="center">
                        {device.parentDevice && !pickedParent && (
                          <P1Regular color="gray.5">{device.parentDevice.deviceName} /</P1Regular>
                        )}
                        <P1Regular fw={device.structure === 'parent' ? 500 : 400}>{device.deviceName}</P1Regular>
                      </Group>
                      <DeviceStructureIcon structure={device.structure} />
                    </Group>
                    {(!pickedParent || device.structure === 'parent') && (
                      <>
                        <Group spacing={8} px={16}>
                          <RevisionTermSelect
                            w="calc(100% - 24px)"
                            autoSelectSingleResult={context === 'create'}
                            options={device.revisionPlan}
                            name={`objekto-devices.${device.deviceId}.termId`}
                            placeholder="Bez termínu"
                            clearable
                            {...form.getInputProps(`devices.${device.deviceId}.termId`)}
                          />
                          <QuestionMarkTooltip
                            multiline
                            width={275}
                            label={
                              kind === 'revision'
                                ? 'Vyberte termín zariadenia, pre ktorý sa má vykonať revízia alebo nechajte bez termínu ak ide o mimoriadnu revíziu.'
                                : 'Vyberte termín zariadenia, pre ktorý sa má vypracovať formulár alebo nechajte bez termínu ak sa vypracovanie neviaže na termín.'
                            }
                          />
                        </Group>
                        <Box px={10} style={{ justifySelf: 'end' }}>
                          <Button
                            leftIcon={<IconTrashX stroke="1.5" />}
                            variant="danger-secondary"
                            onClick={() => confirmDeleteDevice(device.deviceId)}
                            disabled={readOnly.devices}
                          >
                            Odobrať
                          </Button>
                        </Box>
                      </>
                    )}
                  </Group>
                ))}
              </Stack>
              {!readOnly.devices && !pickedParent && (
                <Box>
                  <Button variant="subtle" onClick={addDevices} leftIcon={<IconPlus stroke="1.5" />}>
                    Pridať
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </FormInputGroup>
      )}
    </FormWrapper>
  );
}
